/** How many location codes to show inline before collapsing the rest. */
export const PRICE_OPTION_LOCATION_PREVIEW = 3;

/**
 * Group warehouse price options by identical (selling price, MRP) pairs.
 */
export function groupPriceOptionsByPair(options = []) {
  const map = new Map();

  for (const option of options) {
    const unitCost =
      option.unit_cost != null && option.unit_cost !== ""
        ? Number(option.unit_cost)
        : null;
    const unitPrice =
      option.unit_price != null && option.unit_price !== ""
        ? Number(option.unit_price)
        : null;
    const key = `${unitCost ?? "null"}|${unitPrice ?? "null"}`;

    if (!map.has(key)) {
      map.set(key, {
        unit_cost: Number.isFinite(unitCost) ? unitCost : null,
        unit_price: Number.isFinite(unitPrice) ? unitPrice : null,
        location_codes: [],
      });
    }

    const code = String(option.location_code || "").trim();
    if (code) {
      map.get(key).location_codes.push(code);
    }
  }

  return Array.from(map.values())
    .map((group) => ({
      ...group,
      location_codes: [...new Set(group.location_codes)].sort(),
    }))
    .sort(
      (a, b) =>
        b.location_codes.length - a.location_codes.length ||
        (Number(a.unit_cost) || 0) - (Number(b.unit_cost) || 0),
    );
}

export function formatAmount(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Compact location summary for a price group.
 */
export function summarizeLocationCodes(
  codes = [],
  previewCount = PRICE_OPTION_LOCATION_PREVIEW,
) {
  const unique = [...new Set(codes.filter(Boolean))].sort();
  if (!unique.length) {
    return {
      count: 0,
      previewText: "Unknown location",
      hasMore: false,
      hiddenCount: 0,
      all: [],
    };
  }
  const preview = unique.slice(0, previewCount);
  const hiddenCount = Math.max(0, unique.length - preview.length);
  const previewText =
    hiddenCount > 0
      ? `${preview.join(", ")} +${hiddenCount} more`
      : preview.join(", ");

  return {
    count: unique.length,
    previewText,
    hasMore: hiddenCount > 0,
    hiddenCount,
    all: unique,
  };
}

export function formatLocationCountLabel(count) {
  if (count <= 0) return "Unknown location";
  if (count === 1) return "1 location";
  return `${count} locations`;
}
